import type { InvoiceSummary, PaymentStatusName } from "@/api/invoices";
import { Badge } from "@/components/ui/badge";

const STATUSES: Record<
  PaymentStatusName,
  { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive" | "muted" }
> = {
  draft: { label: "Draft", variant: "warning" },
  issued: { label: "Issued", variant: "secondary" },
  sent: { label: "Sent", variant: "default" },
  partially_paid: { label: "Partly paid", variant: "warning" },
  paid: { label: "Paid", variant: "success" },
  credited: { label: "Credited", variant: "muted" },
  closed: { label: "Closed", variant: "muted" },
  cancelled: { label: "Cancelled", variant: "destructive" },
};

export function statusLabel(status: PaymentStatusName): string {
  return STATUSES[status].label;
}

export function overdueLabel(days: number): string {
  return days === 1 ? "1 day overdue" : `${days} days overdue`;
}

type BadgeFacts = Pick<
  InvoiceSummary,
  "status" | "is_overdue" | "days_overdue" | "awaiting_bank_letter"
>;

/** The payment status, plus "N days overdue" or "Bank letter pending" when they apply. */
export function InvoiceStatusBadges({ invoice }: { invoice: BadgeFacts }) {
  const { label, variant } = STATUSES[invoice.status];
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Badge variant={variant}>{label}</Badge>
      {invoice.is_overdue && <Badge variant="destructive">{overdueLabel(invoice.days_overdue)}</Badge>}
      {invoice.awaiting_bank_letter && <Badge variant="muted">Bank letter pending</Badge>}
    </span>
  );
}
